import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { listRcsByCliente } from '../../requisicoes-compra/api';
import {
  useLinkProjetoRequisicao,
  useProjetoRequisicoes,
  useUnlinkProjetoRequisicao,
} from './requisicoes.api';

function formatMoney(raw: number | string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return '—';
  const n = Number(String(raw).replace(',', '.'));
  if (!Number.isFinite(n)) return String(raw);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export interface ProjectRequisicoesDialogProps {
  open: boolean;
  clientId: number;
  projectId: number;
  projectNome: string;
  onClose: () => void;
}

/**
 * Lado reverso do vínculo RC↔Projeto: a partir de um projeto, gerenciar as RCs
 * vinculadas. Espelha `RcProjetosDialog`. Card [pusd8Icv].
 */
export function ProjectRequisicoesDialog({
  open,
  clientId,
  projectId,
  projectNome,
  onClose,
}: ProjectRequisicoesDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();

  const vinculadasQuery = useProjetoRequisicoes(clientId, projectId);
  const disponiveisQuery = useQuery({
    queryKey: ['purchase-requests', 'by-cliente', clientId],
    queryFn: () => listRcsByCliente(clientId),
    enabled: open && clientId > 0,
    staleTime: 15_000,
  });
  const link = useLinkProjetoRequisicao(clientId, projectId);
  const unlink = useUnlinkProjetoRequisicao(clientId, projectId);

  const [selecionada, setSelecionada] = useState<number | ''>('');

  const vinculadas = vinculadasQuery.data ?? [];
  const vinculadasSet = useMemo(
    () => new Set(vinculadas.map((v) => v.requisicaoCompraCodigo)),
    [vinculadas],
  );
  const disponiveis = useMemo(
    () => (disponiveisQuery.data ?? []).filter((rc) => !vinculadasSet.has(rc.id)),
    [disponiveisQuery.data, vinculadasSet],
  );

  const isBusy = link.isPending || unlink.isPending;

  async function handleAdd(): Promise<void> {
    if (selecionada === '') return;
    try {
      await link.mutateAsync(selecionada);
      setSelecionada('');
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function handleRemove(codigo: number): Promise<void> {
    try {
      await unlink.mutateAsync(codigo);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: () => setSelecionada('') }}
    >
      <DialogTitle>
        {t('features.clientes.projetos.requisicoes.title', { projeto: projectNome })}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('features.clientes.projetos.requisicoes.descricao')}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              select
              size="small"
              fullWidth
              label={t('features.clientes.projetos.requisicoes.adicionarLabel')}
              value={selecionada === '' ? '' : String(selecionada)}
              onChange={(e) => setSelecionada(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={disponiveisQuery.isFetching || disponiveis.length === 0}
              helperText={
                disponiveis.length === 0 && !disponiveisQuery.isFetching
                  ? t('features.clientes.projetos.requisicoes.semDisponiveis')
                  : undefined
              }
            >
              <MenuItem value="">
                <em>{t('features.clientes.projetos.requisicoes.selecionar')}</em>
              </MenuItem>
              {disponiveis.map((rc) => (
                <MenuItem key={rc.id} value={String(rc.id)}>
                  {rc.referencia} · {formatMoney(rc.saldo)}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => void handleAdd()}
              disabled={selecionada === '' || isBusy}
              sx={{ mt: 0.5, flexShrink: 0 }}
            >
              {t('features.clientes.projetos.requisicoes.adicionar')}
            </Button>
          </Stack>

          {vinculadasQuery.isLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : vinculadas.length === 0 ? (
            <Alert severity="info" variant="outlined">
              {t('features.clientes.projetos.requisicoes.vazio')}
            </Alert>
          ) : (
            <List dense disablePadding>
              {vinculadas.map((v) => (
                <ListItem
                  key={v.requisicaoCompraCodigo}
                  divider
                  secondaryAction={
                    <Tooltip title={t('features.clientes.projetos.requisicoes.remover')}>
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          disabled={isBusy}
                          aria-label={t('features.clientes.projetos.requisicoes.removerAria', {
                            rc: v.requisicaoCompraReferencia,
                          })}
                          onClick={() => void handleRemove(v.requisicaoCompraCodigo)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                >
                  <ListItemText
                    primary={v.requisicaoCompraReferencia}
                    secondary={`${formatMoney(v.saldo)} · ${v.status}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
